"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { authClient } from "@/lib/auth-client";

export interface SessionUser {
	id: string;
	pubkey?: string;
	email?: string;
	name?: string;
}

interface SessionResult {
	user: SessionUser;
	raw: unknown;
}

export const SESSION_QUERY_KEY = ["session"] as const;

/**
 * Read the session. By default this lets Better Auth answer from its
 * short-lived cookie cache (no database or KV round trip); pass
 * `fresh: true` only at transitions where the server state just changed
 * (sign-in, consent, identity switch) and the cookie may lag behind.
 */
async function fetchSession(fresh: boolean): Promise<SessionResult | null> {
	const session = await authClient.getSession(
		fresh ? { query: { disableCookieCache: true } } : undefined
	);
	const user = session?.data?.user as SessionUser | undefined;
	if (!user) {
		return null;
	}
	return { user, raw: session };
}

/**
 * Session hook backed by TanStack Query.
 *
 * - Shows stale data instantly on re-mount (no blank flash)
 * - Revalidates in background on window focus / visibility change
 * - Shared across Navbar, home page, unlock gate, etc. via query key, so a
 *   page load costs one session request instead of one per consumer
 */
export function useSession() {
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: SESSION_QUERY_KEY,
		queryFn: () => fetchSession(false),
		staleTime: 30 * 1000, // 30 seconds before background refetch
		gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
		retry: false,
	});

	/**
	 * Force a fresh server read (bypassing the cookie cache) and publish it to
	 * every subscriber. Use after sign-in, sign-out, or consent.
	 */
	const invalidate = () =>
		queryClient.fetchQuery({
			queryKey: SESSION_QUERY_KEY,
			queryFn: () => fetchSession(true),
			staleTime: 0,
		});

	return {
		user: query.data?.user ?? null,
		/** Raw session object for getActiveOrganizationId() etc. */
		session: query.data?.raw ?? null,
		isLoading: query.isLoading,
		isStale: query.isStale,
		error: query.error,
		invalidate,
	};
}

/**
 * Owner check hook - only runs when user is authenticated.
 */
export function useIsOwner(userId: string | undefined) {
	return useQuery({
		queryKey: ["is-owner", userId],
		queryFn: async (): Promise<boolean> => {
			const response = await fetch("/api/user/is-owner");
			if (!response.ok) {
				return false;
			}
			const { isOwner } = await response.json();
			return isOwner;
		},
		enabled: !!userId,
		staleTime: 5 * 60 * 1000, // 5 minutes
		retry: false,
	});
}
